import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskCard, TaskRow } from './TaskCard';
import { TaskDetailPanel, TaskDetail } from './TaskDetailPanel';
import { ValueSheet } from './ValueSheet';
import { UndoToast } from './UndoToast';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

const STAGES = ['backlog', 'in_progress', 'review'] as const;
type Stage = typeof STAGES[number];

/**
 * TaskBoard — #62 Phase 2 task_board (UXeng's per-screen spec §4) + the Tasks-complete-loop (cmdr/
 * UXeng, 2026-08-23: create-from-header -> list -> tap -> detail -> status via pill/sheet -> persists
 * -> back in the list, + one-tap reversible Complete, + delete). Desktop/tablet: the board, 3 fixed
 * columns — tablet scrolls HORIZONTALLY INSIDE the board region only (the outer page never does).
 * Mobile: a segmented stage picker (each with its count) + that stage's cards in a SINGLE column —
 * never a shrunken board, since a board at 390px is one column seen through a keyhole.
 *
 * Stage change is AttributePill -> bottom sheet on phone, -> popover on desktop/tablet — the SAME tap
 * path on every tier. Drag is not built this pass (desktop may add it later as an ADDITIONAL
 * affordance, never the only one — the tap path must exist regardless).
 */
export const TaskBoard: React.FC = () => {
  const { t } = useTranslation('common');
  // X-1 via useListFetch (P38 surface 6 of 10). Board mutations are all optimistic-in-place
  // (setBoard below), so no action-driven revalidate signal is needed here.
  const list = useListFetch<Record<Stage, TaskRow[]>>('/api/tasks/board', {
    map: (res) => ({ backlog: res.backlog ?? [], in_progress: res.in_progress ?? [], review: res.review ?? [] }),
  });
  const board = list.data ?? { backlog: [], in_progress: [], review: [] };
  const setBoard = (fn: (b: Record<Stage, TaskRow[]>) => Record<Stage, TaskRow[]>) =>
    list.setData((d) => fn(d ?? { backlog: [], in_progress: [], review: [] }));
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileStage, setMobileStage] = useState<Stage>('backlog');
  const [editing, setEditing] = useState<{ task: TaskRow; anchor: HTMLElement; shape: 'sheet' | 'popover' } | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'undo' | 'error'; onUndo?: () => void } | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | 'new' | null>(null);

  const stageLabel = (s: Stage) => t(`taskBoard.stages.${s}`);
  const stageOptions = STAGES.map((s) => ({ value: s, label: stageLabel(s) }));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  const findStage = (taskId: string): Stage | null => {
    for (const s of STAGES) if (board[s].some((t) => t.id === taskId)) return s;
    return null;
  };

  const applyStage = async (task: TaskRow, next: Stage) => {
    const prev = findStage(task.id);
    if (!prev || prev === next) { setEditing(null); return; }
    // Optimistic move between columns.
    setBoard((b) => ({ ...b, [prev]: b[prev].filter((x) => x.id !== task.id), [next]: [task, ...b[next]] }));
    setEditing(null);
    try {
      const res = await fetch(`/api/tasks/${task.id}/stage`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: next }) });
      if (!res.ok) throw new Error(String(res.status));
      setToast({ message: t('taskBoard.toast.updated'), variant: 'undo', onUndo: () => { setToast(null); void applyStage(task, prev); } });
    } catch {
      setBoard((b) => ({ ...b, [next]: b[next].filter((x) => x.id !== task.id), [prev]: [task, ...b[prev]] })); // revert
      setToast({ message: t('taskBoard.toast.failed'), variant: 'error' });
    }
  };

  const onEditStage = (task: TaskRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => setEditing({ task, anchor, shape });

  // One-tap Complete/Undo from the LIST CARD (no detail panel open) — optimistic + a 5s undo toast,
  // same shape as applyStage. The detail panel's own Complete/Undo chip does NOT go through this path
  // (it already IS a persistent toggle in place, so no ephemeral toast is needed there).
  const toggleCompleteFromCard = async (task: TaskRow) => {
    const stage = findStage(task.id);
    if (!stage) return;
    const wasComplete = !!task.completedAt;
    const optimisticAt = wasComplete ? null : new Date().toISOString();
    const patchRow = (at: string | null) => setBoard((b) => ({ ...b, [stage]: b[stage].map((x) => x.id === task.id ? { ...x, completedAt: at } : x) }));
    patchRow(optimisticAt);
    try {
      const res = await fetch(`/api/tasks/${task.id}/${wasComplete ? 'uncomplete' : 'complete'}`, { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      if (!wasComplete) {
        setToast({ message: t('taskBoard.toast.completed'), variant: 'undo', onUndo: () => { setToast(null); void toggleCompleteFromCard({ ...task, completedAt: optimisticAt }); } });
      }
    } catch {
      patchRow(task.completedAt); // revert
      setToast({ message: t('taskBoard.toast.completeFailed'), variant: 'error' });
    }
  };

  const patchRowEverywhere = (updated: TaskDetail) => {
    setBoard((b) => {
      const next: Record<Stage, TaskRow[]> = { backlog: [], in_progress: [], review: [] };
      for (const s of STAGES) next[s] = b[s].filter((x) => x.id !== updated.id);
      const stage = (STAGES as readonly string[]).includes(updated.status) ? (updated.status as Stage) : null;
      if (stage) {
        const existing = b[stage].find((x) => x.id === updated.id);
        const row: TaskRow = {
          id: updated.id, title: updated.title, dueDate: updated.dueDate, completedAt: updated.completedAt,
          ownerId: updated.ownerId, ownerName: updated.ownerName,
          parentId: existing?.parentId ?? null, parentTitle: existing?.parentTitle ?? null,
        };
        next[stage] = [row, ...next[stage]];
      }
      return next;
    });
  };

  const removeRowEverywhere = (taskId: string) => {
    setBoard((b) => {
      const next: Record<Stage, TaskRow[]> = { backlog: [], in_progress: [], review: [] };
      for (const s of STAGES) next[s] = b[s].filter((x) => x.id !== taskId);
      return next;
    });
  };

  const openCreate = () => setDetailTaskId('new');
  const openDetail = (taskId: string) => setDetailTaskId(taskId);
  const closeDetail = () => setDetailTaskId(null);

  const header = (
    <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
      <button type="button" data-testid="task-new" onClick={openCreate} aria-label={t('taskBoard.newTask')}
        className="inline-flex items-center justify-center gap-1.5 h-9 min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
        <span aria-hidden>+</span>
        <span className="hidden md:inline">{t('taskBoard.newTask')}</span>
      </button>
    </div>
  );

  const detailPanel = detailTaskId && (
    <div className="fixed inset-0 z-40 lg:static lg:inset-auto lg:w-96 lg:shrink-0 lg:border-s lg:border-border-neutral bg-canvas">
      <TaskDetailPanel
        taskId={detailTaskId}
        sheetShape={isMobile ? 'sheet' : 'popover'}
        onClose={closeDetail}
        onCreated={(created) => { patchRowEverywhere(created); }}
        onUpdated={(updated) => { patchRowEverywhere(updated); }}
        onDeleted={(id) => { removeRowEverywhere(id); closeDetail(); }}
      />
    </div>
  );

  // X-1: loading and failure render through the shared shell (header kept, so the create action and
  // tier chrome never vanish); the per-column empties below only ever render on a trustworthy board.
  if (list.status !== 'ready') {
    return (
      <div className="flex flex-col h-full">
        {header}
        <ListStateShell status={list.status} testIdBase="tasks-board" onRetry={list.retry}>{null}</ListStateShell>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex flex-col h-full lg:flex-row">
        <div className="flex flex-col h-full flex-1 min-w-0">
          {header}
          <div className="flex gap-1 p-2 border-b border-border-neutral">
            {STAGES.map((s) => (
              <button key={s} onClick={() => setMobileStage(s)}
                data-testid={`task-stage-picker-${s}`}
                className={`flex-1 h-9 rounded-lg text-xs font-semibold ${mobileStage === s ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>
                {stageLabel(s)} ({board[s].length})
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {board[mobileStage].map((task) => (
              <TaskCard key={task.id} task={task} stageLabel={stageLabel(mobileStage)} onEditStage={onEditStage}
                onOpenDetail={openDetail} onToggleComplete={toggleCompleteFromCard} sheetShape="sheet" />
            ))}
            {board[mobileStage].length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('taskBoard.empty')}</div>}
          </div>
          {editing && (
            <ValueSheet shape="sheet" title={`${t('taskBoard.stage')} — ${editing.task.title}`} options={stageOptions} current={findStage(editing.task.id)}
              onSelect={(v) => v && applyStage(editing.task, v as Stage)} onClose={() => setEditing(null)} />
          )}
          {toast && <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
        </div>
        {detailPanel}
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="relative flex-1 min-w-0 overflow-x-auto flex flex-col">
        {header}
        <div className="flex gap-4 p-4 h-full min-w-max">
          {STAGES.map((s) => (
            <div key={s} className="w-80 flex-shrink-0 flex flex-col bg-canvas rounded-xl">
              <div className="px-3 py-2 text-label-caps uppercase text-text-muted">{stageLabel(s)} ({board[s].length})</div>
              <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2">
                {board[s].map((task) => (
                  <TaskCard key={task.id} task={task} stageLabel={stageLabel(s)} onEditStage={onEditStage}
                    onOpenDetail={openDetail} onToggleComplete={toggleCompleteFromCard} sheetShape="popover" />
                ))}
              </div>
            </div>
          ))}
        </div>
        {editing && (
          <ValueSheet shape="popover" title={`${t('taskBoard.stage')} — ${editing.task.title}`} options={stageOptions} current={findStage(editing.task.id)}
            anchor={editing.anchor} onSelect={(v) => v && applyStage(editing.task, v as Stage)} onClose={() => setEditing(null)} />
        )}
        {toast && <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
      </div>
      {detailPanel}
    </div>
  );
};
